import { BrandStatus, KeyType } from '@seek/brand-types';
import type { Dispatch } from 'redux';

import {
  TEALIUM_AD_TYPE,
  TEALIUM_CAJA_ABOUT_BUSINESS_CHARACTER_COUNT,
  TEALIUM_CAJA_PAY_TYPE,
  TEALIUM_FINAL_JOB_SUMMARY_CHARACTER_COUNT,
  TEALIUM_TEXT_INPUT_LIST_UPDATE,
} from 'src/app/shared/constants/dispatcherId';
import { getClassificationDataBySeekIds } from 'src/app/utils/classifications';
import { isValidExpiryDate } from 'src/app/utils/date';
import { isPositiveNumber } from 'src/app/utils/isPositiveNumber';
import tealium from 'src/app/v1/state/dispatcher/tealium';
import { actionUpdateWithDraftId } from 'src/app/v1/state/modules/app/withDraftId';
import { actionUpdateAboutBusiness } from 'src/app/v1/state/modules/formData/aboutBusiness';
import { actionUpdateApplicationFormUrl } from 'src/app/v1/state/modules/formData/applicationFormUrl';
import { actions as actionBenefits } from 'src/app/v1/state/modules/formData/benefits';
import { actionUpdateBrand } from 'src/app/v1/state/modules/formData/brand';
import { actionUpdateCategoryId } from 'src/app/v1/state/modules/formData/categoryId';
import { actionUpdateDisplayPay } from 'src/app/v1/state/modules/formData/displayPay';
import { actionUpdateExpiryDate } from 'src/app/v1/state/modules/formData/expiryDate';
import { actionUpdateHasAIAssistanceBeenUsed } from 'src/app/v1/state/modules/formData/hasAIAssistanceBeenUsed';
import { actionUpdateIsConfidential } from 'src/app/v1/state/modules/formData/isConfidential';
import { actionUpdateIsGuided } from 'src/app/v1/state/modules/formData/isGuided';
import { actionUpdateJobAdReference } from 'src/app/v1/state/modules/formData/jobAdReference';
import { actionUpdateJobDescription } from 'src/app/v1/state/modules/formData/jobDescription';
import { actionUpdateJobLocationId } from 'src/app/v1/state/modules/formData/jobLocationId';
import { actionUpdateJobSummary } from 'src/app/v1/state/modules/formData/jobSummary';
import { actionUpdateJobTitle } from 'src/app/v1/state/modules/formData/jobTitle';
import { actionUpdateKeySellingPoint1 } from 'src/app/v1/state/modules/formData/keySellingPoint1';
import { actionUpdateKeySellingPoint2 } from 'src/app/v1/state/modules/formData/keySellingPoint2';
import { actionUpdateKeySellingPoint3 } from 'src/app/v1/state/modules/formData/keySellingPoint3';
import { actionUpdateLinkOutApplyVia } from 'src/app/v1/state/modules/formData/linkOutApplyVia';
import { actionUpdateMaxSalary } from 'src/app/v1/state/modules/formData/maxSalary';
import { actionUpdateMinSalary } from 'src/app/v1/state/modules/formData/minSalary';
import { actionUpdatePayType } from 'src/app/v1/state/modules/formData/payType';
import { actionUpdatePhoneNumber } from 'src/app/v1/state/modules/formData/phoneNumber';
import { actions as actionQualifications } from 'src/app/v1/state/modules/formData/qualifications';
import { actionUpdateQuestionnaireUrl } from 'src/app/v1/state/modules/formData/questionnaireUrl';
import { actions as actionResponsibilities } from 'src/app/v1/state/modules/formData/responsibilities';
import { actionUpdateSalaryCurrency } from 'src/app/v1/state/modules/formData/salaryCurrency';
import { actionUpdateSalaryText } from 'src/app/v1/state/modules/formData/salaryText';
import { actionUpdateSelectedProductId } from 'src/app/v1/state/modules/formData/selectedProductId';
import { actionUpdateSourceProductId } from 'src/app/v1/state/modules/formData/sourceProductId';
import { actionUpdateSubCategoryId } from 'src/app/v1/state/modules/formData/subCategoryId';
import { actionUpdateVideoUrl } from 'src/app/v1/state/modules/formData/videoUrl';
import { actionUpdateWorkArrangement } from 'src/app/v1/state/modules/formData/workArrangement';
import { actionUpdateWorkType } from 'src/app/v1/state/modules/formData/workType';
import { actionUpdateCategoryText } from 'src/app/v1/state/modules/session/categoryText';
import { actionUpdateSeekClassificationId } from 'src/app/v1/state/modules/session/seekClassificationId';
import { actionUpdateSeekSubClassificationId } from 'src/app/v1/state/modules/session/seekSubClassificationId';
import { actionUpdateSubCategoryText } from 'src/app/v1/state/modules/session/subCategoryText';
import { actionUpdateCajaHasAIAssistanceBeenUsed } from 'src/app/v1/state/modules/tealium/cajaHasAIAssistanceBeenUsed';
import { actionUpdateCajaSalaryCurrency } from 'src/app/v1/state/modules/tealium/cajaSalaryCurrency';
import { actionUpdateCajaWorkType } from 'src/app/v1/state/modules/tealium/cajaWorkType';
import { actionUpdateTrackingDisplayPay } from 'src/app/v1/state/modules/tealium/displayPay';
import { actionUpdateIsLinkOutApplyViaSeek } from 'src/app/v1/state/modules/tealium/isLinkOutApplyViaSeek';
import { actionUpdateIsPrivateAd } from 'src/app/v1/state/modules/tealium/isPrivateAd';
import { actionUpdateJobAdClassification } from 'src/app/v1/state/modules/tealium/jobAdClassification';
import { actionUpdateJobAdSubClassification } from 'src/app/v1/state/modules/tealium/jobAdSubClassification';
import { actionUpdateProductSetProductIds } from 'src/app/v1/state/modules/tealium/productSetProductIds';
import { actionUpdateRoleTitle } from 'src/app/v1/state/modules/tealium/roleTitle';
import type { DraftPayload } from 'src/types/draft';
import { ApplyViaOptions } from 'src/types/linkOut';

import { actionUpdateCajaWorkplaceOption } from '../../../modules/tealium/cajaWorkplaceOption';

export default (data: DraftPayload) => (dispatch: Dispatch) => {
  const dispatchTealium: any = tealium;

  if (data.aboutBusiness) {
    dispatch(actionUpdateAboutBusiness(data.aboutBusiness));
    dispatch(
      dispatchTealium(
        TEALIUM_CAJA_ABOUT_BUSINESS_CHARACTER_COUNT,
        data.aboutBusiness,
      ),
    );
  }

  if (data.productDisplayName) {
    dispatch(dispatchTealium(TEALIUM_AD_TYPE, data.productDisplayName));
  }

  // If loading a draft that has a selected product then reload it
  // into redux as the selected product and product set
  if (data.selectedProductId) {
    dispatch(actionUpdateSelectedProductId(data.selectedProductId));
    dispatch(actionUpdateSourceProductId(data.selectedProductId));
    dispatch(actionUpdateProductSetProductIds([data.selectedProductId]));
  }

  if (data.benefits?.length >= 1) {
    dispatch(actionBenefits.addMultipleItems(data.benefits));
    dispatch(
      dispatchTealium(TEALIUM_TEXT_INPUT_LIST_UPDATE, {
        count: data.benefits.length,
        target: 'BENEFITS',
      }),
    );
  }

  if (data.brandId) {
    dispatch(
      actionUpdateBrand({
        id: data.brandId,
        assets: {
          cover: undefined,
          logo: {
            id: '',
            strategies: {},
            url: '',
          },
        },
        name: '',
        owner: {
          id: '',
          type: KeyType.ADVERTISER_ID,
        },
        createdOn: '',
        state: BrandStatus.ACTIVE,
        legacyId: 0,
      }),
    );
  }

  // categoryId and subCategoryId are SEEK ids (not adcentre ones)
  if (Boolean(data.categoryId) && Boolean(data.subCategoryId)) {
    // Get all the classification data by using the SEEK category and subcategory ids
    const classification = getClassificationDataBySeekIds(
      data.categoryId as number,
      data.subCategoryId as number,
    );

    if (classification) {
      dispatch(actionUpdateCategoryId(String(classification.classificationId)));

      dispatch(
        actionUpdateSubCategoryId(String(classification.subClassificationId)),
      );

      dispatch(
        actionUpdateCategoryText(classification.classificationText ?? ''),
      );
      dispatch(
        actionUpdateSeekClassificationId(
          classification.seekClassificationId ?? '',
        ),
      );
      dispatch(
        actionUpdateJobAdClassification(
          classification.classificationText ?? '',
        ),
      );
      dispatch(
        actionUpdateSubCategoryText(classification.subClassificationText ?? ''),
      );
      dispatch(
        actionUpdateSeekSubClassificationId(
          classification.seekSubClassificationId ?? '',
        ),
      );
      dispatch(
        actionUpdateJobAdSubClassification(
          classification.subClassificationText ?? '',
        ),
      );
    }
  }

  dispatch(actionUpdateDisplayPay(data.displayPay === true));
  dispatch(actionUpdateTrackingDisplayPay(data.displayPay === true));

  const hasAIAssistanceBeenUsed = data.hasAIAssistanceBeenUsed === true;
  dispatch(actionUpdateHasAIAssistanceBeenUsed(hasAIAssistanceBeenUsed));
  dispatch(actionUpdateCajaHasAIAssistanceBeenUsed(hasAIAssistanceBeenUsed));

  if (data.expiryDate && isValidExpiryDate(data.expiryDate)) {
    dispatch(actionUpdateExpiryDate(data.expiryDate));
  }

  dispatch(actionUpdateIsGuided(data.isGuided === true));

  if (data.internalReference) {
    dispatch(actionUpdateJobAdReference(data.internalReference));
  }

  if (data.isConfidential !== undefined) {
    dispatch(actionUpdateIsConfidential(data.isConfidential));
    dispatch(actionUpdateIsPrivateAd(data.isConfidential));
  }

  if (data.jobDescription) {
    dispatch(actionUpdateJobDescription(data.jobDescription));
  }

  if (data.jobLocationId) {
    dispatch(actionUpdateJobLocationId(data.jobLocationId.toString()));
  }

  if (data.jobSummary) {
    dispatch(actionUpdateJobSummary(data.jobSummary));
    dispatch(
      dispatchTealium(
        TEALIUM_FINAL_JOB_SUMMARY_CHARACTER_COUNT,
        data.jobSummary,
      ),
    );
  }

  if (data.jobTitle) {
    dispatch(actionUpdateJobTitle(data.jobTitle));
    dispatch(actionUpdateRoleTitle(data.jobTitle));
  }

  if (data.keySellingPoint1) {
    dispatch(actionUpdateKeySellingPoint1(data.keySellingPoint1));
  }

  if (data.keySellingPoint2) {
    dispatch(actionUpdateKeySellingPoint2(data.keySellingPoint2));
  }

  if (data.keySellingPoint3) {
    dispatch(actionUpdateKeySellingPoint3(data.keySellingPoint3));
  }

  if (data.applicationFormUrl) {
    dispatch(actionUpdateApplicationFormUrl(data.applicationFormUrl));
    dispatch(actionUpdateLinkOutApplyVia(ApplyViaOptions.EXTERNAL_WEBSITE));
    dispatch(
      actionUpdateIsLinkOutApplyViaSeek(ApplyViaOptions.EXTERNAL_WEBSITE),
    );
  }

  if (isPositiveNumber(data.maxSalary)) {
    dispatch(actionUpdateMaxSalary(data.maxSalary!));
  }

  if (isPositiveNumber(data.minSalary)) {
    dispatch(actionUpdateMinSalary(data.minSalary!));
  }

  if (data.payType) {
    dispatch(actionUpdatePayType(String(data.payType)));
    dispatch(dispatchTealium(TEALIUM_CAJA_PAY_TYPE, data.payType));
  }

  if (data.phoneNumber) {
    dispatch(actionUpdatePhoneNumber(String(data.phoneNumber)));
  }

  if (data.qualifications?.length >= 1) {
    dispatch(actionQualifications.addMultipleItems(data.qualifications));
    dispatch(
      dispatchTealium(TEALIUM_TEXT_INPUT_LIST_UPDATE, {
        count: data.qualifications.length,
        target: 'QUALIFICATIONS',
      }),
    );
  }

  if (data.questionnaireUrl) {
    dispatch(actionUpdateQuestionnaireUrl(data.questionnaireUrl));
  }

  if (data.responsibilities?.length >= 1) {
    dispatch(actionResponsibilities.addMultipleItems(data.responsibilities));
    dispatch(
      dispatchTealium(TEALIUM_TEXT_INPUT_LIST_UPDATE, {
        count: data.responsibilities.length,
        target: 'RESPONSIBILITIES',
      }),
    );
  }

  if (data.salaryCurrency) {
    dispatch(actionUpdateSalaryCurrency(data.salaryCurrency));
    dispatch(actionUpdateCajaSalaryCurrency(data.salaryCurrency));
  }

  if (data.salaryText) {
    dispatch(actionUpdateSalaryText(data.salaryText));
  }

  if (data.videoUrl) {
    dispatch(actionUpdateVideoUrl(data.videoUrl));
  }

  // default to null if no work arrangements (cover case Pre-Launch Workplace Option)
  dispatch(actionUpdateWorkArrangement(data.workArrangements?.[0] ?? null));
  dispatch(actionUpdateCajaWorkplaceOption(data.workArrangements?.[0] ?? null));

  if (data.workType) {
    dispatch(actionUpdateWorkType(data.workType));
    dispatch(actionUpdateCajaWorkType(data.workType));
  }

  dispatch(actionUpdateWithDraftId(true));
};
